/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\IVRSettingsTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Incoming Call Tools module.
----------------------------------------------------------------*/

import React from 'react';
import { Settings } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import Toggle from '../../../components/ui/Toggle';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import MohSelect from '../../../components/ui/MohSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select-shadcn';
import { LanguageSelector } from '../../extensions/components/LanguageSelector';
import { useI18n } from '../../../hooks/useI18n';
import { IVRSettings } from '../types/ivr.types';

interface SettingsFormData {
  settings: IVRSettings;
}

interface IVRSettingsTabProps {
  formData: SettingsFormData;
  setFormData: React.Dispatch<React.SetStateAction<SettingsFormData>>;
  errors: Record<string, string>;
}

export function IVRSettingsTab({ formData, setFormData, errors }: IVRSettingsTabProps) {
  const { t } = useI18n();

  const updateSetting = (key: keyof IVRSettings, value: unknown) =>
    setFormData(prev => ({ ...prev, settings: { ...prev.settings, [key]: value } }));

  return (
    <FormBox title={t('ivrs:sections.settings')} icon={<Settings className="h-5 w-5" />} className="mt-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">

        <Label htmlFor="backgroundMusic">
          <SimpleTooltip content={t('ivrs:fields.backgroundMusic.tooltip')}>{t('ivrs:fields.backgroundMusic.label')}</SimpleTooltip>
        </Label>
        <div>
          <MohSelect value={formData.settings.backgroundMusic} onChange={(value) => updateSetting('backgroundMusic', value)} placeholder="Select Music on Hold class..." />
        </div>

        <Label htmlFor="inputMethod">
          <SimpleTooltip content={t('ivrs:fields.inputMethod.tooltip')}>{t('ivrs:fields.inputMethod.label')}</SimpleTooltip>
        </Label>
        <div>
          <Select value={formData.settings.inputMethod} onValueChange={(value) => updateSetting('inputMethod', value)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="dtmf">{t('ivrs:fields.inputMethod.options.dtmf')}</SelectItem>
              <SelectItem value="speech">{t('ivrs:fields.inputMethod.options.speech')}</SelectItem>
              <SelectItem value="dual">{t('ivrs:fields.inputMethod.options.dual')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Label htmlFor="minDigits">
          <SimpleTooltip content={t('ivrs:fields.minDigits.tooltip')}>{t('ivrs:fields.minDigits.label')}<span className="text-red-500 ml-1">*</span></SimpleTooltip>
        </Label>
        <div>
          <Input id="minDigits" type="number" min="1" max="10" value={formData.settings.minDigits}
            onChange={(e) => updateSetting('minDigits', parseInt(e.target.value) || 1)} className={errors.minDigits ? 'border-red-500' : ''} />
          {errors.minDigits && <p className="text-sm text-red-500 mt-1">{errors.minDigits}</p>}
        </div>

        <Label htmlFor="maxDigits">
          <SimpleTooltip content={t('ivrs:fields.maxDigits.tooltip')}>{t('ivrs:fields.maxDigits.label')}<span className="text-red-500 ml-1">*</span></SimpleTooltip>
        </Label>
        <div>
          <Input id="maxDigits" type="number" min="1" max="10" value={formData.settings.maxDigits}
            onChange={(e) => updateSetting('maxDigits', parseInt(e.target.value) || 1)} className={errors.maxDigits ? 'border-red-500' : ''} />
          {errors.maxDigits && <p className="text-sm text-red-500 mt-1">{errors.maxDigits}</p>}
        </div>

        <Label>
          <SimpleTooltip content={t('ivrs:fields.inputLanguage.tooltip')}>{t('ivrs:fields.inputLanguage.label')}</SimpleTooltip>
        </Label>
        <div>
          <LanguageSelector value={formData.settings.inputLanguage || 'en-us'} onChange={(value) => updateSetting('inputLanguage', value)}
            selectedVoice="" onVoiceChange={() => {}} onSoundPrefixChange={() => {}} showVoiceSelector={false} disabled={false} showLabel={false} />
        </div>

        <Label>
          <SimpleTooltip content={t('ivrs:fields.beepBeforeInput.tooltip')}>{t('ivrs:fields.beepBeforeInput.label')}</SimpleTooltip>
        </Label>
        <div><Toggle value={formData.settings.beepBeforeInput} onChange={(checked) => updateSetting('beepBeforeInput', checked)} /></div>

        <Label>
          <SimpleTooltip content={t('ivrs:fields.recordSession.tooltip')}>{t('ivrs:fields.recordSession.label')}</SimpleTooltip>
        </Label>
        <div><Toggle value={formData.settings.recordSession} onChange={(checked) => updateSetting('recordSession', checked)} /></div>

        <Label htmlFor="maxCallDuration">
          <SimpleTooltip content={t('ivrs:fields.maxCallDuration.tooltip')}>{t('ivrs:fields.maxCallDuration.label')}<span className="text-red-500 ml-1">*</span></SimpleTooltip>
        </Label>
        <div>
          <Input id="maxCallDuration" type="number" min="30" max="3600" value={formData.settings.maxCallDuration}
            onChange={(e) => updateSetting('maxCallDuration', parseInt(e.target.value) || 1800)} className={errors.maxCallDuration ? 'border-red-500' : ''} />
          {errors.maxCallDuration && <p className="text-sm text-red-500 mt-1">{errors.maxCallDuration}</p>}
        </div>

        <Label htmlFor="logLevel">
          <SimpleTooltip content={t('ivrs:fields.logLevel.tooltip')}>{t('ivrs:fields.logLevel.label')}</SimpleTooltip>
        </Label>
        <div>
          <Select value={formData.settings.logLevel} onValueChange={(value) => updateSetting('logLevel', value)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="info">{t('ivrs:fields.logLevel.options.info')}</SelectItem>
              <SelectItem value="debug">{t('ivrs:fields.logLevel.options.debug')}</SelectItem>
              <SelectItem value="warning">{t('ivrs:fields.logLevel.options.warning')}</SelectItem>
              <SelectItem value="error">{t('ivrs:fields.logLevel.options.error')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {formData.settings.inputMethod !== 'dtmf' && (
          <>
            <Label htmlFor="speechGrammar">
              <SimpleTooltip content={t('ivrs:fields.speechGrammar.tooltip')}>{t('ivrs:fields.speechGrammar.label')}</SimpleTooltip>
            </Label>
            <div className="col-span-3">
              <Input id="speechGrammar" value={formData.settings.speechGrammar}
                onChange={(e) => updateSetting('speechGrammar', e.target.value)} placeholder="/usr/share/freeswitch/grammars/main_menu.jsgf" />
            </div>
          </>
        )}
      </div>
    </FormBox>
  );
}
